import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { ObservableMap, ObservableSet } from 'mobx';
import { observer } from 'mobx-react';

import {
    AnvilSelectOptionsProps,
    Banner,
    BodyText,
    Button,
    ButtonGroup,
    Card,
    Dialog,
    Divider,
    Eyebrow,
    Form,
    Headline,
    Label,
    Layout,
    Link as AnvilLink,
    Modal,
    Page,
    Stack,
} from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';

import { SiteMode, environment } from '../../api';
import {
    AppApprovalStatus,
    AppCategory,
    AppBuilder,
    DeveloperType,
    SecretManagementOption,
} from '../../api/app.api';
import { CopyBox, CustomModal, INTEGRATIONS_TEAM_EMAIL, PageSpinner, RadioButtonGroup } from '../../common';
import { WebhookEventViewModel } from '../models/webhook-event-view-model';
import { AppDetailsBanner, AppDetailsPageStore } from '../stores/app-details-page.store';
import { ApiScopesDrawer } from './api-scopes-drawer';
import { AppAvailabilityTable } from './app-availability-table';
import { AppPageHeader } from './app-page-header';
import { AppScopesVersionsDrawer } from './app-scopes-versions-drawer';
import { AppVersionStatusTag } from './app-version-status-tag';
import { DocumentSelectIcon } from './icons';
import { WebhookEventsDrawer } from './webhook-events-drawer';
import { WebhookSignatureKeyEditableDisplay } from './webhook-signature-key-editable-display';

export interface AppDetailsPageProps {
    id?: number;
}

const AppDetailsBannerSection: FC<{ banner: AppDetailsBanner }> = ({ banner }) => {
    switch (banner) {
        case AppDetailsBanner.RegistrationRequestReceived:
            return (
                <Layout.Section>
                    <Banner status="info" icon title="App Registration Request Received">
                        Our team will review your request within <strong>2 business days</strong>. You’ll
                        receive an email notification once your app has been approved or rejected. If you
                        have questions in the meantime, please contact{' '}
                        <AnvilLink href={`mailto:${INTEGRATIONS_TEAM_EMAIL}`} primary>
                            {INTEGRATIONS_TEAM_EMAIL}
                        </AnvilLink>
                        .
                    </Banner>
                </Layout.Section>
            );
        case AppDetailsBanner.ThirdPartyRegistrationRequestReceived:
            return (
                <Layout.Section>
                    <Banner status="info" icon title="App Registration Request Received">
                        Our team will review your submission and notify you by email once a decision has
                        been made.
                        <ul>
                            <li>You can continue testing the APIs using your integration environment.</li>
                            <li>
                                Customer tenants will be able to connect to this app once it has been
                                approved.
                            </li>
                        </ul>
                    </Banner>
                </Layout.Section>
            );
        case AppDetailsBanner.Created:
            return (
                <Layout.Section>
                    <Banner status="success" icon title="App registered.">
                        Your application key is shown below.
                    </Banner>
                </Layout.Section>
            );
        default:
            return null;
    }
};
AppDetailsBannerSection.displayName = 'AppDetailsBannerSection';

/*
 * Description field helper text. The approval-oriented prompts apply only to Customer Apps, which go
 * through a specific review flow: pending apps see the expanded prompt encouraging detail to speed up
 * approval, and approved apps see the short prompt. All other developer types keep the standard prompt.
 */
const STANDARD_DESCRIPTION_HELPER_TEXT =
    'Describe your app in 100 words and provide a use case to showcase how it benefits ServiceTitan customers.';
const APPROVED_DESCRIPTION_HELPER_TEXT = 'Explain what your app does and the business problem it solves.';
const PENDING_DESCRIPTION_HELPER_TEXT =
    'Explain what your app does and the business problem it solves. The more detail you provide, ' +
    'the faster we can review and approve your submission — incomplete descriptions may delay or ' +
    'prevent approval.';

/* Label for the Customer App "who builds this" select; also referenced by the missing-field banners. */
const APP_BUILDER_FIELD_LABEL = 'Who will build and maintain this integration?';

/*
 * Options for the App Builder Customer App select. The value is the AppBuilder enum; the description
 * renders as supporting text under each label.
 */
const appBuilderOptionData: {
    value: AppBuilder;
    label: string;
    description: string;
}[] = [
    {
        value: AppBuilder.Customer,
        label: 'Internal IT team',
        description: "Your organization's own developers or IT staff",
    },
    {
        value: AppBuilder.ThirdParty,
        label: 'A third-party organization',
        description:
            'An independent software company that built a product or tool they are offering to ' +
            'you — you are their customer',
    },
    {
        value: AppBuilder.Contractor,
        label: 'A contractor or IT services firm',
        description:
            'An individual or agency you have hired specifically to build this integration — ' +
            'they are working on your behalf, not selling you their own software',
    },
];

export const AppDetailsPage: FC<AppDetailsPageProps> = provide({
    singletons: [AppDetailsPageStore],
})(
    observer(({ id = 0 }) => {
        const [store] = useDependencies(AppDetailsPageStore);
        const history = useHistory();

        const isTenantApisSiteMode = environment.site.mode === SiteMode.TenantApis;
        const isTenantApiGroup = store.selectedApiGroup?.name === 'tenant';
        const isCustomerApp = store.developerType === DeveloperType.Customer;
        const showsApprovalStatus =
            isTenantApisSiteMode && store.developerType === DeveloperType.ThirdParty;

        const appCategoryOptions = useMemo(() => {
            const createCategoryOptions = (
                appCategories: AppCategory[],
                parentName: string,
            ): AnvilSelectOptionsProps[] =>
                appCategories.map(c => ({
                    value: c.id,
                    text: parentName + c.name,
                    content: c.children.length > 0 ? <Eyebrow>{c.name}</Eyebrow> : c.name,
                    readOnly: c.children.length > 0,
                    options:
                        c.children.length > 0
                            ? createCategoryOptions(c.children, c.name + '\u2002▶\u2002')
                            : undefined,
                }));
            return createCategoryOptions(store.appCategories, '');
        }, [store.appCategories]);
        const [expandedAppCategoryIds, setExpandedAppCategoryIds] = useState<number[]>([]);

        const appBuilderOptions = useMemo<AnvilSelectOptionsProps[]>(
            () =>
                appBuilderOptionData.map(option => ({
                    value: option.value,
                    text: option.label,
                    content: (
                        <Stack direction="column">
                            <BodyText>{option.label}</BodyText>
                            <BodyText size="small" subdued>
                                {option.description}
                            </BodyText>
                        </Stack>
                    ),
                })),
            [],
        );

        const apiGroupOptions = useMemo(
            () => store.apiGroups.map(g => ({ text: g.displayName, value: g.name })),
            [store.apiGroups],
        );

        const handleConfirmDelete = useCallback(async () => {
            await store.deleteApp();
            history.replace('/apps');
        }, [store, history]);

        const handleSave = useCallback(async () => {
            if (store.id > 0) {
                if (await store.saveExistingApp()) {
                    history.push('/apps');
                }
            } else {
                const url = await store.saveNewApp();
                if (url) {
                    history.replace(url);
                }
            }
            if (store.form.hasError) {
                document.querySelector('.Page__page')?.scrollTo(0, 0);
            }
        }, [store, history]);

        useEffect(() => {
            store.load(id).then(() => {
                // Expand all categories by default:
                setExpandedAppCategoryIds(
                    store.appCategories.filter(c => c.children.length > 0).map(c => c.id),
                );
            });
        }, [store, id]);

        const [appScopesVersionsDrawerOpen, setAppScopesVersionsDrawerOpen] = useState(false);
        const [selectedAuthScopes, setSelectedAuthScopes] = useState<Set<string> | null>(null); // not null => drawer is open
        const [webhookEventViewModels, setWebhookEventViewModels] = useState<Map<
            string,
            WebhookEventViewModel
        > | null>(null); // not null => drawer is open

        if (store.loading) {
            return <PageSpinner />;
        }

        const formState = store.form.$;

        /*
         * Banners prompting existing apps to fill in fields added after they were first registered.
         * App Builder is only required for Customer apps, so it is listed/prompted only for them.
         */
        const isExistingTenantApp = isTenantApisSiteMode && id > 0 && !store.deleted;
        const missingOriginalNewFields =
            !formState.emailAddress.$ || !formState.description.$ || formState.appCategoryId.$ === null;
        const missingAppBuilderOnly =
            isCustomerApp && !missingOriginalNewFields && formState.appBuilder.$ === null;

        const pageHeader = (
            <AppPageHeader
                id={id}
                publicId={store.publicId}
                appName={formState.appName.$}
                deleted={store.deleted}
                errorBannerVisible={store.errorBannerVisible}
            />
        );

        if (store.errorBannerVisible) {
            return (
                <Page header={pageHeader}>
                    <Layout>
                        <Banner status="critical" icon title="An error occurred while loading this page." />
                    </Layout>
                </Page>
            );
        }

        const externalDataGuidHelp =
            'App GUID to be used when reading or writing key-value pairs in APIs that support external data';

        const createApiScopesButton = (type: 'add' | 'edit') => (
            <Button
                primary
                outline={type === 'edit'}
                iconName={type === 'edit' ? 'edit' : undefined}
                disabled={store.deleted}
                onClick={() => {
                    setSelectedAuthScopes(new ObservableSet(formState.authScopes.value));
                }}
            >
                {`${type === 'add' ? 'Add' : 'Edit'} API Scopes`}
            </Button>
        );

        const createWebhookEventsButton = (type: 'add' | 'edit') => (
            <Button
                primary
                outline={type === 'edit'}
                iconName={type === 'edit' ? 'edit' : undefined}
                disabled={store.deleted}
                onClick={() => {
                    setWebhookEventViewModels(
                        new ObservableMap(
                            formState.webhookEvents.value.map(event => [
                                event.name,
                                new WebhookEventViewModel(true, event.url),
                            ]),
                        ),
                    );
                }}
            >
                {`${type === 'add' ? 'Add' : 'Edit'} Webhook Events`}
            </Button>
        );

        return (
            <Page
                header={pageHeader}
                footer={
                    <Stack className="w-100" justifyContent="flex-end">
                        {store.deleted ? (
                            <Button
                                primary
                                onClick={() => {
                                    history.push('/apps?deleted');
                                }}
                            >
                                Back
                            </Button>
                        ) : (
                            <ButtonGroup>
                                <Button
                                    onClick={() => {
                                        history.push('/apps');
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    primary
                                    loading={store.deleting || store.saving}
                                    onClick={handleSave}
                                >
                                    {id > 0 ? 'Save' : 'Register App'}
                                </Button>
                            </ButtonGroup>
                        )}
                    </Stack>
                }
            >
                <Layout autoSections={false}>
                    <AppDetailsBannerSection banner={store.visibleBanner} />
                    {isExistingTenantApp && missingOriginalNewFields && (
                        <Layout.Section>
                            <Banner
                                status="info"
                                icon
                                title="Please fill out the following newly added fields:"
                            >
                                <ul>
                                    <li>Email Address</li>
                                    <li>Description</li>
                                    {isCustomerApp && <li>{APP_BUILDER_FIELD_LABEL}</li>}
                                    <li>App Category</li>
                                </ul>
                            </Banner>
                        </Layout.Section>
                    )}
                    {isExistingTenantApp && missingAppBuilderOnly && (
                        <Layout.Section>
                            <Banner
                                status="info"
                                icon
                                title="Please fill out the following newly added field:"
                            >
                                <ul>
                                    <li>{APP_BUILDER_FIELD_LABEL}</li>
                                </ul>
                            </Banner>
                        </Layout.Section>
                    )}
                    {store.form.hasError && (
                        <Layout.Section>
                            <Banner status="critical" icon title="Please correct the errors shown below." />
                        </Layout.Section>
                    )}
                    {store.deleted && (
                        <Layout.Section>
                            <Banner
                                status="info"
                                icon
                                title="This app has been deleted and cannot be modified."
                            />
                        </Layout.Section>
                    )}
                    <Form className="w-100">
                        <Layout.Section>
                            <Card>
                                <Card.Section>
                                    <Headline>Developer Details</Headline>
                                </Card.Section>
                                <Card.Section>
                                    <Form.Group widths="equal">
                                        <Form.Input
                                            label="Organization Name"
                                            labelProps={{
                                                required: true,
                                            }}
                                            maxLength={120}
                                            readOnly={store.deleted}
                                            value={formState.organizationName.value}
                                            error={formState.organizationName.error}
                                            onChange={formState.organizationName.onChangeHandler}
                                        />
                                        <Form.Input
                                            label="Organization/App Website"
                                            labelProps={{
                                                required: true,
                                            }}
                                            maxLength={250}
                                            placeholder="https://my-org.com/"
                                            type="url"
                                            readOnly={store.deleted}
                                            value={formState.homepageUrl.value}
                                            error={formState.homepageUrl.error}
                                            onChange={formState.homepageUrl.onChangeHandler}
                                        />
                                        {isTenantApisSiteMode && (
                                            <Form.Input
                                                label="Email Address"
                                                labelProps={{
                                                    required: true,
                                                    help: 'Who can ServiceTitan contact about this app if needed?',
                                                }}
                                                maxLength={100}
                                                type="email"
                                                readOnly={store.deleted}
                                                value={formState.emailAddress.value}
                                                error={formState.emailAddress.error}
                                                onChange={formState.emailAddress.onChangeHandler}
                                            />
                                        )}
                                    </Form.Group>
                                </Card.Section>
                            </Card>
                        </Layout.Section>
                        <Layout.Section>
                            <Card>
                                <Card.Section>
                                    <Headline>App Details</Headline>
                                </Card.Section>
                                <Card.Section>
                                    <Form.Input
                                        label="App Name"
                                        labelProps={{
                                            required: true,
                                        }}
                                        maxLength={120}
                                        readOnly={store.deleted}
                                        value={formState.appName.value}
                                        error={formState.appName.error}
                                        onChange={formState.appName.onChangeHandler}
                                    />
                                    {isTenantApisSiteMode && (
                                        <Form.TextArea
                                            label="Description"
                                            labelProps={{ required: true }}
                                            description={
                                                !isCustomerApp
                                                    ? STANDARD_DESCRIPTION_HELPER_TEXT
                                                    : store.approvalStatus === AppApprovalStatus.Approved
                                                      ? APPROVED_DESCRIPTION_HELPER_TEXT
                                                      : PENDING_DESCRIPTION_HELPER_TEXT
                                            }
                                            maxLength={10000}
                                            readOnly={store.deleted}
                                            showCounter
                                            defaultValue={formState.description.$ /* for the counter */}
                                            value={formState.description.value}
                                            error={formState.description.error}
                                            onChange={formState.description.onChangeHandler}
                                        />
                                    )}
                                    {isCustomerApp && (
                                        <Form.AnvilSelect
                                            label={APP_BUILDER_FIELD_LABEL}
                                            labelProps={{ required: true }}
                                            options={appBuilderOptions}
                                            disabled={store.deleted}
                                            value={appBuilderOptions.find(
                                                option => option.value === formState.appBuilder.value,
                                            )}
                                            error={formState.appBuilder.error}
                                            onChange={(data: AnvilSelectOptionsProps | undefined) => {
                                                formState.appBuilder.onChange(
                                                    data ? (data.value as AppBuilder) : null,
                                                );
                                            }}
                                        />
                                    )}
                                    {isTenantApisSiteMode && (
                                        <Form.AnvilSelect
                                            label="App Category"
                                            labelProps={{ required: true }}
                                            scrollHeight="calc(50dvh - 30px)"
                                            options={appCategoryOptions}
                                            tree={{
                                                onExpand: (id: number) => {
                                                    setExpandedAppCategoryIds(
                                                        expandedAppCategoryIds.includes(id)
                                                            ? expandedAppCategoryIds.filter(
                                                                  existingId => existingId !== id,
                                                              )
                                                            : [...expandedAppCategoryIds, id],
                                                    );
                                                },
                                                collapseValue: expandedAppCategoryIds,
                                            }}
                                            disabled={store.deleted}
                                            value={
                                                formState.appCategoryId.value
                                                    ? appCategoryOptions
                                                          .concat(
                                                              appCategoryOptions
                                                                  .filter(o => o.options)
                                                                  .map(o => o.options),
                                                          )
                                                          .flat(1)
                                                          .find(
                                                              o =>
                                                                  o.value === formState.appCategoryId.value,
                                                          )
                                                    : undefined
                                            }
                                            error={formState.appCategoryId.error}
                                            onChange={(data: AnvilSelectOptionsProps) => {
                                                formState.appCategoryId.onChange(data ? data.value : null);
                                            }}
                                        />
                                    )}
                                    {store.apiGroups.length !== 1 && (
                                        <Form.AnvilSelect
                                            label="API Category"
                                            labelProps={{ required: true }}
                                            trigger={{ clearable: false }}
                                            emptyResult="You do not have access to any API categories."
                                            options={apiGroupOptions}
                                            disabled={id > 0}
                                            value={formState.apiGroup.value}
                                            error={formState.apiGroup.error}
                                            onChange={store.handleApiGroupChange}
                                        />
                                    )}
                                    {id > 0 ? (
                                        <Stack direction="row" spacing={4} wrap="wrap">
                                            <Form.Field
                                                label="Application Key"
                                                labelProps={{
                                                    help: 'Value of the ST-App-Key request header to be passed in every API call',
                                                }}
                                            >
                                                <CopyBox size={30} value={store.applicationKey1!} />
                                            </Form.Field>
                                            {isTenantApiGroup && (
                                                <Form.Field
                                                    label="External Data Application GUID"
                                                    labelProps={{ help: externalDataGuidHelp }}
                                                >
                                                    <CopyBox
                                                        size={36}
                                                        value={formState.externalDataGuid.value}
                                                    />
                                                </Form.Field>
                                            )}
                                        </Stack>
                                    ) : (
                                        isTenantApiGroup && (
                                            <Form.Input
                                                label="External Data Application GUID"
                                                labelProps={{
                                                    required: true,
                                                    help: externalDataGuidHelp,
                                                }}
                                                maxLength={40}
                                                value={formState.externalDataGuid.value}
                                                error={formState.externalDataGuid.error}
                                                onChange={formState.externalDataGuid.onChangeHandler}
                                                action={
                                                    <Button onClick={store.handleGenerateExternalDataGuid}>
                                                        Generate
                                                    </Button>
                                                }
                                            />
                                        )
                                    )}
                                </Card.Section>
                            </Card>
                        </Layout.Section>
                        {store.selectedApiGroup && (
                            <Layout.Section>
                                <Card>
                                    <Card.Section>
                                        <Label required help="Permissions required by this app">
                                            <Headline inline>
                                                {`${store.selectedApiGroup.displayName} APIs${
                                                    store.selectedApiGroupHasWebhooks ? ' and Webhooks' : ''
                                                }`}
                                            </Headline>
                                        </Label>
                                        {formState.authScopes.error && (
                                            <div className="FormField__errorText">
                                                {formState.authScopes.error}
                                            </div>
                                        )}
                                    </Card.Section>
                                    <Card.Section>
                                        <Stack direction="column" spacing={2}>
                                            {id > 0 && (
                                                <Stack.Item>
                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                        <Button
                                                            onClick={() => {
                                                                setAppScopesVersionsDrawerOpen(true);
                                                            }}
                                                        >
                                                            View Version History
                                                        </Button>
                                                        {store.hasPendingScopesVersion ? (
                                                            <BodyText size="small" subdued>
                                                                Pending Version
                                                            </BodyText>
                                                        ) : (
                                                            <Stack
                                                                direction="row"
                                                                spacing={1}
                                                                alignItems="center"
                                                            >
                                                                <BodyText size="small" subdued>
                                                                    {`Version ${store.scopesVersions[0].version}`}
                                                                </BodyText>
                                                                {showsApprovalStatus && (
                                                                    <AppVersionStatusTag
                                                                        version={store.scopesVersions[0]}
                                                                        subtle
                                                                        compact
                                                                    />
                                                                )}
                                                            </Stack>
                                                        )}
                                                    </Stack>
                                                </Stack.Item>
                                            )}
                                            <Divider alignContent="start">
                                                <Eyebrow size="large" className="c-neutral-400 m-r-1">
                                                    APIs
                                                </Eyebrow>
                                            </Divider>
                                            {formState.authScopes.value.size === 0 ? (
                                                <Fragment>
                                                    <Stack.Item alignSelf="center">
                                                        {DocumentSelectIcon}
                                                    </Stack.Item>
                                                    <Stack.Item className="ta-center">
                                                        <Headline size="small" className="m-b-1">
                                                            You haven’t added any API scopes for this app
                                                        </Headline>
                                                        <BodyText size="small" subdued>
                                                            Select the API scopes required by this app.
                                                        </BodyText>
                                                    </Stack.Item>
                                                    <Stack.Item alignSelf="center">
                                                        {createApiScopesButton('add')}
                                                    </Stack.Item>
                                                </Fragment>
                                            ) : (
                                                <Fragment>
                                                    <Eyebrow>Scopes</Eyebrow>
                                                    <Stack.Item>
                                                        <ul className="m-y-0">
                                                            <li>Read: {store.scopeDisplayNames.read}</li>
                                                            <li className="m-t-2">
                                                                Write: {store.scopeDisplayNames.write}
                                                            </li>
                                                        </ul>
                                                    </Stack.Item>
                                                    <Stack.Item>{createApiScopesButton('edit')}</Stack.Item>
                                                </Fragment>
                                            )}
                                            {store.selectedApiGroupHasWebhooks && (
                                                <Fragment>
                                                    <Divider alignContent="start">
                                                        <Eyebrow
                                                            size="large"
                                                            className="c-neutral-400 m-r-1"
                                                        >
                                                            Webhooks
                                                        </Eyebrow>
                                                    </Divider>
                                                    {formState.webhookEvents.value.length === 0 ? (
                                                        <Fragment>
                                                            <Stack.Item alignSelf="center">
                                                                {DocumentSelectIcon}
                                                            </Stack.Item>
                                                            <Stack.Item className="ta-center">
                                                                <Headline size="small" className="m-b-1">
                                                                    You haven’t added any webhook events for
                                                                    this app
                                                                </Headline>
                                                                <BodyText size="small" subdued>
                                                                    Select the webhook events required by
                                                                    this app.
                                                                </BodyText>
                                                            </Stack.Item>
                                                            <Stack.Item alignSelf="center">
                                                                {createWebhookEventsButton('add')}
                                                            </Stack.Item>
                                                        </Fragment>
                                                    ) : (
                                                        <Fragment>
                                                            <Eyebrow>Events</Eyebrow>
                                                            <Stack.Item>
                                                                <ul className="m-y-0">
                                                                    <li>
                                                                        {'Receive: '}
                                                                        {store.webhookEventDisplayNames}
                                                                    </li>
                                                                </ul>
                                                            </Stack.Item>
                                                            <Stack.Item>
                                                                {createWebhookEventsButton('edit')}
                                                            </Stack.Item>
                                                            <Divider />
                                                            <Stack.Item>
                                                                <WebhookSignatureKeyEditableDisplay
                                                                    fieldState={
                                                                        formState.webhookSignatureKey
                                                                    }
                                                                    isReadonly={
                                                                        store.isWebhookSignatureKeyReadonly
                                                                    }
                                                                    appDeleted={store.deleted}
                                                                    onDelete={
                                                                        store.handleDeleteWebhookSignatureKey
                                                                    }
                                                                />
                                                            </Stack.Item>
                                                        </Fragment>
                                                    )}
                                                </Fragment>
                                            )}
                                        </Stack>
                                    </Card.Section>
                                </Card>
                            </Layout.Section>
                        )}
                        {store.selectedApiGroup && (
                            <Layout.Section>
                                <Card>
                                    <Card.Section>
                                        <Label required help="Owners of the data this app will access">
                                            <Headline inline>
                                                {store.selectedApiGroup.name === 'tenant'
                                                    ? 'Tenants + Customer Global Networks'
                                                    : `${store.selectedApiGroup.displayName} ${store.selectedApiGroup.resourceOwnerTermPlural}`}
                                            </Headline>
                                        </Label>
                                        {formState.appAvailabilities.error && (
                                            <div className="FormField__errorText">
                                                {formState.appAvailabilities.error}
                                            </div>
                                        )}
                                    </Card.Section>
                                    <Card.Section>
                                        <Stack direction="column" spacing={2}>
                                            <Stack.Item>
                                                <AppAvailabilityTable
                                                    resourceOwnerType={
                                                        store.selectedApiGroup.resourceOwnerTerm
                                                    }
                                                    readOnly={store.deleted}
                                                    fieldState={formState.appAvailabilities}
                                                />
                                            </Stack.Item>
                                            <Stack.Item>
                                                <Button
                                                    primary
                                                    outline
                                                    iconName="add"
                                                    disabled={store.deleted}
                                                    onClick={store.handleAddResourceOwner}
                                                >
                                                    {`Add ${store.selectedApiGroup.resourceOwnerTerm}`}
                                                </Button>
                                            </Stack.Item>
                                        </Stack>
                                    </Card.Section>
                                    {store.selectedApiGroup.maxNetworkCount > 0 && (
                                        <Card.Section>
                                            <Stack direction="column" spacing={2}>
                                                <Stack.Item>
                                                    <AppAvailabilityTable
                                                        resourceOwnerType="Network"
                                                        readOnly={store.deleted}
                                                        fieldState={formState.networkAppAvailabilities}
                                                    />
                                                </Stack.Item>
                                                <Stack.Item>
                                                    <Button
                                                        primary
                                                        outline
                                                        iconName="add"
                                                        disabled={
                                                            store.deleted ||
                                                            formState.networkAppAvailabilities.value
                                                                .length >=
                                                                store.selectedApiGroup.maxNetworkCount
                                                        }
                                                        onClick={store.handleAddNetwork}
                                                    >
                                                        Add Network
                                                    </Button>
                                                </Stack.Item>
                                            </Stack>
                                        </Card.Section>
                                    )}
                                </Card>
                            </Layout.Section>
                        )}
                        {isTenantApiGroup && (
                            <Layout.Section>
                                <Card>
                                    <Card.Section>
                                        <Headline>Client Credentials Management</Headline>
                                    </Card.Section>
                                    <Card.Section>
                                        <RadioButtonGroup<SecretManagementOption>
                                            name="secretManagementOption"
                                            description="To access ServiceTitan APIs, your app must supply a client ID and client secret. Who will configure these credentials within the app?"
                                            disabled={store.deleted}
                                            value={formState.secretManagementOption.value}
                                            options={[
                                                {
                                                    label: 'I, the app developer, will configure the credentials on behalf of each tenant',
                                                    description: (
                                                        <span>
                                                            Select this option if you will securely store
                                                            the credentials in a file, database, or key
                                                            vault. After a tenant connects your app, you can
                                                            view the client ID and generate the client
                                                            secret by going to “
                                                            <Link
                                                                to="/apps"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                My Apps
                                                            </Link>
                                                            ” and clicking “View Connections”.
                                                        </span>
                                                    ),
                                                    value: SecretManagementOption.Developer,
                                                },
                                                {
                                                    label: 'Tenants will configure the credentials themselves',
                                                    description:
                                                        'Select this option if tenants will input the credentials through your app’s configuration screen. Tenants can obtain these credentials by signing in to ServiceTitan and going to Settings > Integrations > API Application Access.',
                                                    value: SecretManagementOption.ResourceOwner,
                                                },
                                            ]}
                                            onChange={formState.secretManagementOption.onChangeHandler}
                                        />
                                        <Banner status="warning" icon title="Security Note">
                                            You must keep client IDs and client secrets confidential. Never
                                            publish them with your source code, embed them within web pages,
                                            or distribute them via an app store.
                                        </Banner>
                                    </Card.Section>
                                </Card>
                            </Layout.Section>
                        )}
                        {id > 0 && !store.deleted && (
                            <Layout.Section>
                                <Card>
                                    <Card.Section>
                                        <Headline>Delete Application</Headline>
                                    </Card.Section>
                                    <Card.Section>
                                        <Stack direction="column" spacing={2}>
                                            <Stack.Item>
                                                <BodyText>Permanently delete this application.</BodyText>
                                            </Stack.Item>
                                            <Stack.Item>
                                                <Button
                                                    negative
                                                    outline
                                                    loading={store.deleting}
                                                    onClick={store.handleDelete}
                                                >
                                                    Delete
                                                </Button>
                                            </Stack.Item>
                                        </Stack>
                                    </Card.Section>
                                </Card>
                            </Layout.Section>
                        )}
                    </Form>
                    {appScopesVersionsDrawerOpen && (
                        <AppScopesVersionsDrawer
                            scopesVersions={store.scopesVersions}
                            showsApprovalStatus={showsApprovalStatus}
                            onClose={() => {
                                setAppScopesVersionsDrawerOpen(false);
                            }}
                        />
                    )}
                    {store.selectedApiGroup && selectedAuthScopes && (
                        <ApiScopesDrawer
                            apiGroup={store.selectedApiGroup}
                            enabledAuthScopes={store.enabledAuthScopes}
                            hasBlockedAuthScopes={store.selectedApiGroupHasBlockedAuthScopes}
                            selectedAuthScopes={selectedAuthScopes}
                            onSubmit={() => {
                                formState.authScopes.onChange(selectedAuthScopes);
                                setSelectedAuthScopes(null);
                            }}
                            onCancel={() => {
                                setSelectedAuthScopes(null);
                            }}
                        />
                    )}
                    {store.selectedApiGroup && webhookEventViewModels && (
                        <WebhookEventsDrawer
                            apiGroup={store.selectedApiGroup}
                            viewModels={webhookEventViewModels}
                            onSubmit={() => {
                                formState.webhookEvents.onChange(
                                    [...webhookEventViewModels.entries()]
                                        .filter(([_name, { isSelected }]) => isSelected)
                                        .map(([name, { url }]) => ({ name, url })),
                                );
                                formState.authScopes.onChange(formState.authScopes.value); // revalidate
                                setWebhookEventViewModels(null);
                            }}
                            onCancel={() => {
                                setWebhookEventViewModels(null);
                            }}
                        />
                    )}
                    <Dialog
                        title="Confirm Deletion"
                        primaryActionName="Delete"
                        secondaryActionName="Cancel"
                        negative
                        open={store.deleteDialogVisible}
                        onPrimaryActionClick={handleConfirmDelete}
                        onSecondaryActionClick={store.handleCancelDelete}
                        onClose={store.handleCancelDelete}
                    >
                        <BodyText>
                            Are you sure you want to permanently delete the application? This action cannot
                            be undone.
                        </BodyText>
                    </Dialog>
                    {store.confirmScopeRemovalDialogVisible && (
                        <CustomModal
                            dialog
                            size={Modal.Sizes.S}
                            title="Confirm Permanent Scope Removal"
                            primaryActionName="Yes, Continue"
                            secondaryActionName="Cancel"
                            onPrimaryActionClick={handleSave}
                            onSecondaryActionClick={() => {
                                store.confirmScopeRemovalDialogVisible = false;
                            }}
                        >
                            <BodyText>
                                Due to API scope restrictions in effect for your organization, the following
                                scopes cannot be added back later once they are removed:
                            </BodyText>
                            {store.removedExistingBlockedAuthScopes.read && (
                                <BodyText>
                                    <b>{'Read: '}</b>
                                    {store.removedExistingBlockedAuthScopes.read}
                                </BodyText>
                            )}
                            {store.removedExistingBlockedAuthScopes.write && (
                                <BodyText>
                                    <b>{'Write: '}</b>
                                    {store.removedExistingBlockedAuthScopes.write}
                                </BodyText>
                            )}
                            <BodyText>Do you want to continue?</BodyText>
                        </CustomModal>
                    )}
                </Layout>
            </Page>
        );
    }),
);
AppDetailsPage.displayName = 'AppDetailsPage';
